import React, { useState, useRef, useEffect } from "react";
import { Globe } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../hooks/useStore";
import { setLanguage } from "../../store/slices/languageSlice";

const languages = [
  { code: "en", label: "EN", name: "English" },
  { code: "et", label: "EST", name: "Eesti" },
] as const;

const LanguageSwitcher: React.FC = () => {
  const dispatch = useAppDispatch();
  const lang = useAppSelector((s) => s.language.lang);
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    if (isOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const currentLang = languages.find((l) => l.code === lang) ?? languages[0];

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-x-1 p-2 text-gray-600 hover:text-primary hover:bg-gray-100 rounded-lg transition-colors"
        aria-label="Change language"
      >
        <Globe className="h-4 w-4" />
        <span className="text-sm font-medium">{currentLang.label}</span>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-1 w-36 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
          {languages.map((l) => (
            <button
              key={l.code}
              onClick={() => {
                dispatch(setLanguage(l.code));
                setIsOpen(false);
              }}
              className={`flex items-center justify-between w-full px-4 py-2 text-sm transition-colors ${
                lang === l.code
                  ? "text-primary bg-gray-50 font-medium"
                  : "text-gray-700 hover:bg-gray-50"
              }`}
            >
              <span>{l.name}</span>
              <span className="text-xs text-gray-400">{l.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
